import { toast } from "./toast";
import { ask } from "./confirm";

// B6-16: the pages carry no inline scripts or on*= handlers (script-src 'self'), and htmx runs without eval, so the small
// behaviours they held live here, keyed by data- attributes. Listeners capture at the document so they run before the
// element's own htmx listener, as the inline handlers did (e.g. a field is cleared before htmx sends the form).

const on = <K extends keyof DocumentEventMap>(type: K, fn: (ev: DocumentEventMap[K], el: Element) => void) =>
  document.addEventListener(type, (ev) => { if (ev.target instanceof Element) fn(ev, ev.target); }, true);

// A confirm on a form's submit or a button's click; Cancel stops it before htmx or the browser sees it. A button's own
// confirm is asked on its click only (Enter in a field clicks the default button too), so it is never asked twice.
// WP50: the question is the app's own dialog (async), so the first submit or click is held and replayed once on Yes.
on("submit", (ev, el) => {
  const msg = el.getAttribute("data-confirm");
  if (msg === null || !(el instanceof HTMLFormElement)) return;
  if (el.dataset.confirmed) { delete el.dataset.confirmed; return; }
  ev.preventDefault(); ev.stopImmediatePropagation();
  const submitter = (ev as SubmitEvent).submitter;
  void ask(msg, true).then((yes) => { if (yes) { el.dataset.confirmed = "1"; el.requestSubmit(submitter ?? undefined); } });
});

on("click", (ev, target) => {
  if (target.closest('a[href="#"]')) ev.preventDefault();   // menu headings: a link that only opens its children
  const el = target.closest<HTMLElement>("[data-confirm]:not(form), [data-print], [data-reload], [data-fullscreen], [data-show-modal], [data-fkey], [data-toggle-parent], .cb-menus-tabs [data-tab]");
  if (!el) return;
  if (el.matches("[data-confirm]:not(form)")) {
    if (el.dataset.confirmed) { delete el.dataset.confirmed; return; }
    ev.preventDefault(); ev.stopImmediatePropagation();
    void ask(el.getAttribute("data-confirm") ?? "", true).then((yes) => { if (yes) { el.dataset.confirmed = "1"; el.click(); } });
  } else if (el.matches("[data-print]")) {
    const menu = el.closest("details");
    if (menu) menu.open = false;
    window.print();
  } else if (el.matches("[data-reload]")) {
    location.reload();
  } else if (el.matches("[data-fullscreen]")) {
    (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {});
  } else if (el.matches("[data-show-modal]")) {
    document.querySelector<HTMLDialogElement>(`#${el.getAttribute("data-show-modal")}`)?.showModal();
  } else if (el.matches("[data-fkey]")) {
    document.dispatchEvent(new KeyboardEvent("keydown", { key: el.getAttribute("data-fkey") ?? "", bubbles: true }));
  } else if (el.matches("[data-toggle-parent]")) {
    ev.preventDefault();
    el.setAttribute("aria-expanded", String(el.parentElement?.classList.toggle("is-open") ?? false));
  } else {
    document.querySelectorAll(".cb-menus-tabs .cb-tab-btn").forEach((b) => b.classList.remove("active"));
    el.classList.add("active");
    document.querySelectorAll(".cb-menus-body .cb-tab-pane").forEach((p) => p.classList.remove("active"));
    document.getElementById(el.getAttribute("data-tab") ?? "")?.classList.add("active");
  }
});

on("focusin", (_, el) => { if (el instanceof HTMLInputElement && el.hasAttribute("data-select-all")) el.select(); });

on("change", (_, el) => {
  if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
  const form = el.form;
  if (el.hasAttribute("data-submit-on-change")) form?.requestSubmit();
  if (el.hasAttribute("data-clear-on-change") && form) {
    form.querySelectorAll<HTMLInputElement>("input[type=hidden], input[type=text]:not([name=kind])").forEach((f) => { f.value = ""; });
  }
  // Places: only a place has a control branch (a branch or head office controls itself).
  if (el.hasAttribute("data-place-type") && form) {
    const isPlace = el.value === "Place";
    const box = form.querySelector<HTMLInputElement>("[name=ControlText]");
    const star = form.querySelector<HTMLElement>("[data-control-star]");
    if (box) box.disabled = !isPlace;
    if (star) star.hidden = !isPlace;
    if (!isPlace) {
      if (box) box.value = "";
      const id = form.querySelector<HTMLInputElement>("[name=ControlBranchId]");
      if (id) id.value = "";
    }
  }
  // Party rates: the apply-to label follows the party role.
  if (el.hasAttribute("data-role-label") && form) {
    const label = form.querySelector("[data-apply-label]");
    if (label) label.textContent = el.value === "Consignor" ? "To Pay" : el.value === "Consignee" ? "Due, Paid" : "Every pay mode";
  }
  // Quotation template: the system default applies to every party, role and place.
  if (el instanceof HTMLInputElement && el.hasAttribute("data-default-template") && form) {
    for (const [n, id] of [["PartyText", "PartyId"], ["PartyRole", null], ["FromText", "FromPlaceId"]] as const) {
      const f = form.elements.namedItem(n) as HTMLInputElement | HTMLSelectElement | null;
      if (!f) continue;
      f.disabled = el.checked;
      if (el.checked && f instanceof HTMLInputElement) {
        f.value = "";
        const hidden = form.elements.namedItem(id ?? n) as HTMLInputElement | null;
        if (hidden) hidden.value = "";
      }
    }
  }
  // "All branches" stands for an empty list: ticking it clears the boxes, ticking a branch clears it.
  const box = el.closest("[data-branch-checklist]");
  if (box && el instanceof HTMLInputElement) {
    if (el.hasAttribute("data-all-branches")) {
      if (el.checked) box.querySelectorAll<HTMLInputElement>("input[name]").forEach((c) => { c.checked = false; });
      else el.checked = true;
    } else {
      const all = box.querySelector<HTMLInputElement>("[data-all-branches]");
      if (all) all.checked = !box.querySelector("input[name]:checked");
    }
  }
});

// htmx triggers without filter expressions: hx-trigger="kit:enter" (data-kit-enter; ="filled" skips an empty box)
// and hx-trigger="kit:alt-r from:body" for Alt+<letter> (by code, so any keyboard layout works).
on("keydown", (ev, el) => {
  if (ev.key === "Enter" && el instanceof HTMLInputElement && el.hasAttribute("data-kit-enter")) {
    if (el.getAttribute("data-kit-enter") !== "filled" || el.value.trim() !== "") el.dispatchEvent(new CustomEvent("kit:enter"));
  }
  if (ev.altKey && /^Key[A-Z]$/.test(ev.code)) document.body.dispatchEvent(new CustomEvent(`kit:alt-${ev.code.slice(3).toLowerCase()}`));
});

// F7 on a purchase-bill header field opens the customs dialog (kit:fkey does not bubble; capture still sees it).
document.addEventListener("kit:fkey", (ev) => {
  const el = ev.target instanceof Element ? ev.target.closest("[data-f7-opens]") : null;
  if (!el || (ev as CustomEvent<{ key: string }>).detail.key !== "F7") return;
  const dialog = document.getElementById(el.getAttribute("data-f7-opens") ?? "");
  if (!(dialog instanceof HTMLDialogElement)) return;
  dialog.showModal();
  dialog.querySelector<HTMLElement>(`[name="${dialog.getAttribute("data-focus-on-open")}"]`)?.focus();
}, true);

// Closing a dialog that held the focus puts it back on the named field (close does not bubble either).
document.addEventListener("close", (ev) => {
  const dialog = ev.target;
  if (!(dialog instanceof HTMLDialogElement) || !dialog.hasAttribute("data-focus-on-close")) return;
  if (dialog.contains(document.activeElement) || document.activeElement === document.body)
    document.querySelector<HTMLElement>(`[name="${dialog.getAttribute("data-focus-on-close")}"]`)?.focus();
}, true);

document.addEventListener("htmx:afterRequest", (ev) => {
  const el = ev.target instanceof Element ? ev.target : null;
  const detail = (ev as CustomEvent<{ successful: boolean; xhr: XMLHttpRequest }>).detail;
  // Statement: the freight-received dialog opens once its rows are in.
  const dialog = document.getElementById(el?.getAttribute("data-open-on-success") ?? "");
  if (dialog instanceof HTMLDialogElement && detail.successful && !dialog.open) dialog.showModal();
  // Journal: "Copy voucher from" also brings the copied voucher's id and narration.
  if (el?.hasAttribute("data-copy-from")) {
    const id = detail.xhr.getResponseHeader("HX-Copy-Id");
    const narration = detail.xhr.getResponseHeader("HX-Copy-Narration");
    const idBox = document.getElementById("copied-from-id") as HTMLInputElement | null;
    const narrationBox = document.getElementById("jv-narration") as HTMLInputElement | HTMLTextAreaElement | null;
    if (id && idBox) idBox.value = id;
    if (narration !== null && narrationBox) narrationBox.value = narration;
  }
});

// The shell (menu drawer, rail, theme) lives in shell.ts (WP50).

const errorToast = document.getElementById("error-toast");
if (errorToast) toast("error", errorToast.getAttribute("data-error") ?? "");
